import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const CartPage = () => {
  const { cart, updateItem, removeItem, clearCart, totalPrice, itemCount } = useCart();
  const navigate = useNavigate();

  const shippingPrice = totalPrice >= 200 ? 0 : 15;
  const taxPrice = parseFloat((totalPrice * 0.08).toFixed(2));
  const orderTotal = parseFloat((totalPrice + shippingPrice + taxPrice).toFixed(2));

  if (!cart.items || cart.items.length === 0) return (
    <div style={{ paddingTop: '72px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '24px' }}>
      <ShoppingBag size={64} color="var(--border)" />
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: 400 }}>Your cart is empty</h2>
      <p style={{ color: 'var(--white-dim)', fontSize: '14px' }}>Discover our exquisite fragrance collection</p>
      <Link to="/collection" style={{ background: 'var(--gold)', color: 'var(--black)', padding: '12px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em' }}>
        EXPLORE COLLECTION
      </Link>
    </div>
  );

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      <div style={{ background: 'var(--dark)', padding: '40px 0', borderBottom: '1px solid var(--border)', marginBottom: '0' }}>
        <div className="container">
          <p style={{ fontSize: '10px', letterSpacing: '0.3em', color: 'var(--gold)', marginBottom: '8px' }}>SHOPPING</p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 400 }}>Your Cart</h1>
          <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '6px' }}>{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '40px', alignItems: 'start' }}>
          {/* Items */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <button onClick={clearCart} style={{ fontSize: '11px', color: '#ef4444', background: 'none', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Trash2 size={12} /> CLEAR ALL
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              {cart.items.map(item => (
                <div key={item._id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr auto', gap: '20px', alignItems: 'center', background: 'var(--dark-2)', padding: '20px', border: '1px solid var(--border)' }}>
                  <Link to={`/product/${item.product?._id}`}>
                    <div style={{ aspectRatio: '1', background: 'var(--dark-3)', overflow: 'hidden' }}>
                      {item.product?.images?.[0] ? (
                        <img src={item.product.images[0]} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : <div style={{ width: '100%', height: '100%', background: 'var(--dark-3)' }} />}
                    </div>
                  </Link>
                  <div>
                    <p style={{ fontSize: '10px', color: 'var(--gold)', letterSpacing: '0.15em', marginBottom: '4px' }}>{item.product?.brand?.toUpperCase()}</p>
                    <Link to={`/product/${item.product?._id}`}>
                      <p style={{ fontFamily: 'var(--font-display)', fontSize: '18px', marginBottom: '4px' }}>{item.name}</p>
                    </Link>
                    {item.size && <p style={{ fontSize: '11px', color: 'var(--white-dim)' }}>{item.size}</p>}
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--gold)', marginTop: '8px' }}>${item.price}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                    <button onClick={() => removeItem(item._id)} style={{ background: 'none', color: 'var(--white-dim)', transition: 'color 0.3s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--white-dim)'}
                    ><Trash2 size={14} /></button>
                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)' }}>
                      <button onClick={() => updateItem(item._id, item.quantity - 1)} style={{ width: '32px', height: '32px', background: 'none', color: 'var(--white)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Minus size={12} /></button>
                      <span style={{ width: '32px', textAlign: 'center', fontSize: '13px' }}>{item.quantity}</span>
                      <button onClick={() => updateItem(item._id, item.quantity + 1)} style={{ width: '32px', height: '32px', background: 'none', color: 'var(--white)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={12} /></button>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--white-dim)' }}>${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px', position: 'sticky', top: '88px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 400, marginBottom: '24px' }}>Order Summary</h3>
            {[
              ['Subtotal', `$${totalPrice.toFixed(2)}`],
              ['Shipping', shippingPrice === 0 ? 'Free' : `$${shippingPrice}`],
              ['Tax (8%)', `$${taxPrice}`],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13px', color: 'var(--white-dim)' }}>
                <span>{label}</span><span style={{ color: 'var(--white)' }}>{val}</span>
              </div>
            ))}
            {shippingPrice === 0 && <p style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '12px', letterSpacing: '0.05em' }}>✓ Free shipping applied</p>}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '16px', display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-display)', fontSize: '24px' }}>
              <span>Total</span><span style={{ color: 'var(--gold)' }}>${orderTotal}</span>
            </div>
            <button onClick={() => navigate('/checkout')} style={{ width: '100%', background: 'var(--gold)', color: 'var(--black)', padding: '14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: 'pointer', marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--gold)'}
            >PROCEED TO CHECKOUT <ArrowRight size={14} /></button>
            <Link to="/collection" style={{ display: 'block', textAlign: 'center', marginTop: '16px', fontSize: '12px', color: 'var(--white-dim)', letterSpacing: '0.1em' }}>← Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
